// Copyright 2026 Granite (granite.md). All rights reserved.
// Proprietary and confidential. Unauthorized copying is prohibited.
import remarkFrontmatter from "remark-frontmatter";
import remarkGfm from "remark-gfm";
import remarkParse from "remark-parse";
import { unified } from "unified";
import { visit } from "unist-util-visit";
import { parse as parseYaml } from "yaml";
const processor = unified().use(remarkParse).use(remarkGfm).use(remarkFrontmatter, ["yaml"]);
// [[target#heading|alias]] and ![[...]]; the target cannot contain ], | or #.
const WIKILINK = /(!?)\[\[([^\]|#]*)(?:#(\^?)([^\]|]*))?(?:\|([^\]]*))?\]\]/gu;
// #tag: letters, digits, _, -, / (Obsidian); preceded by start or whitespace or "(".
const TAG = /(^|[\s(])#([\p{L}\p{N}_\-/]+)/gu;
const BLOCK_ID = /(?:^|\s)\^([A-Za-z0-9-]+)\s*$/u;
const CALLOUT = /^\[!([^\]\s]+)\]([+-])?[ \t]*(.*)$/u;
function pos(node) {
    return { line: node.position?.start.line ?? 1, offset: node.position?.start.offset ?? 0 };
}
function phrasingText(nodes) {
    let out = "";
    for (const n of nodes) {
        if (n.type === "text" || n.type === "inlineCode")
            out += n.value;
        else if ("children" in n)
            out += phrasingText(n.children);
    }
    return out;
}
function validTag(tag) {
    // Must contain a character that is not a digit, and must not end with "/".
    return /[^\p{N}]/u.test(tag) && !tag.endsWith("/") && !tag.startsWith("/");
}
function stringList(v) {
    if (typeof v === "string")
        return v.split(/[,\s]+/u).filter(Boolean);
    if (Array.isArray(v))
        return v.filter((x) => typeof x === "string" || typeof x === "number").map(String);
    return [];
}
/** Parses a note. */
export function parseNote(source) {
    const tree = processor.parse(source);
    const info = { frontmatter: null, headings: [], links: [], tags: [], aliases: [], blockIds: [], callouts: [] };
    visit(tree, (node) => {
        switch (node.type) {
            case "yaml": {
                try {
                    const fm = parseYaml(node.value);
                    if (fm && typeof fm === "object" && !Array.isArray(fm)) {
                        info.frontmatter = fm;
                    }
                }
                catch (e) {
                    info.frontmatterError = e instanceof Error ? e.message : String(e);
                }
                break;
            }
            case "heading": {
                const h = node;
                info.headings.push({ depth: h.depth, text: phrasingText(h.children).trim(), position: pos(h) });
                break;
            }
            case "link": {
                const l = node;
                if (/^[a-z][a-z0-9+.-]*:/iu.test(l.url) || l.url.startsWith("#") && l.url.length === 1)
                    break; // external
                let url = l.url;
                try {
                    url = decodeURI(url);
                }
                catch {
                    // keep as written
                }
                const hash = url.indexOf("#");
                const target = hash >= 0 ? url.slice(0, hash) : url;
                const frag = hash >= 0 ? url.slice(hash + 1) : undefined;
                const link = {
                    kind: "markdown",
                    target,
                    alias: phrasingText(l.children),
                    raw: source.slice(l.position?.start.offset ?? 0, l.position?.end.offset ?? 0),
                    position: pos(l),
                };
                if (frag !== undefined) {
                    if (frag.startsWith("^"))
                        link.block = frag.slice(1);
                    else
                        link.heading = frag;
                }
                info.links.push(link);
                break;
            }
            case "text": {
                const t = node;
                const base = t.position?.start.offset ?? 0;
                // Wikilinks and embeds. remark leaves them as text since they are not Markdown syntax.
                for (const m of t.value.matchAll(WIKILINK)) {
                    const [raw, bang, target = "", caret, frag, alias] = m;
                    const link = {
                        kind: bang ? "embed" : "wikilink",
                        target: target.trim(),
                        raw,
                        position: { line: t.position?.start.line ?? 1, offset: base + m.index },
                    };
                    if (frag !== undefined && frag !== "") {
                        if (caret)
                            link.block = frag.trim();
                        else
                            link.heading = frag.trim();
                    }
                    if (alias !== undefined)
                        link.alias = alias.trim();
                    info.links.push(link);
                }
                // Tags, outside wikilinks.
                const withoutLinks = t.value.replace(WIKILINK, (s) => " ".repeat(s.length));
                for (const m of withoutLinks.matchAll(TAG)) {
                    const tag = m[2] ?? "";
                    if (validTag(tag)) {
                        info.tags.push({ tag, source: "inline", position: { line: t.position?.start.line ?? 1, offset: base + m.index + (m[1]?.length ?? 0) } });
                    }
                }
                break;
            }
            case "paragraph": {
                const last = node.children.at(-1);
                if (last?.type === "text") {
                    const id = BLOCK_ID.exec(last.value)?.[1];
                    if (id)
                        info.blockIds.push(id);
                }
                break;
            }
            case "blockquote": {
                const first = node.children[0];
                if (first?.type === "paragraph") {
                    const firstText = first.children[0];
                    if (firstText?.type === "text") {
                        const line = firstText.value.split("\n")[0] ?? "";
                        const m = CALLOUT.exec(line);
                        if (m) {
                            const callout = { type: (m[1] ?? "").toLowerCase(), title: (m[3] ?? "").trim(), position: pos(node) };
                            if (m[2] === "+" || m[2] === "-")
                                callout.fold = m[2];
                            info.callouts.push(callout);
                        }
                    }
                }
                break;
            }
        }
    });
    const fm = info.frontmatter;
    if (fm) {
        for (const tag of [...stringList(fm.tags), ...stringList(fm.tag)]) {
            const clean = tag.replace(/^#/u, "");
            if (validTag(clean))
                info.tags.push({ tag: clean, source: "frontmatter" });
        }
        info.aliases = [...stringList(fm.aliases), ...stringList(fm.alias)];
    }
    return info;
}
//# sourceMappingURL=markdown.js.map